import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PermissionString } from '../utils/permissions';

interface TokenPayload extends JwtPayload {
  permissions?: string[];
}

export interface ResolvedAuth {
  isAuthenticated: boolean;
  hasPermission: (permission: PermissionString) => boolean;
}

interface AuthContextType {
  permissions: string[];
  hasPermission: (permission: PermissionString) => boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  getToken: () => Promise<string | null>;
  loginWithRedirect: (options?: { appState?: { returnTo?: string } }) => void;
  logout: (options?: { logoutParams?: { returnTo?: string } }) => void;
  /**
   * Resolves once the initial Auth0 + permissions load has completed, with a
   * snapshot of the resolved auth state. Stable across renders — safe to call
   * from a TanStack Router `beforeLoad` (which has snapshot semantics for
   * `context`) to await initial auth without polling or `router.invalidate`.
   */
  whenReady: () => Promise<ResolvedAuth>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const {
    isAuthenticated,
    isLoading: auth0IsLoading,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0();

  const [permissions, setPermissions] = useState<string[]>([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  useEffect(() => {
    if (auth0IsLoading) {
      return;
    }

    if (!isAuthenticated) {
      setPermissions([]);
      setPermissionsLoaded(true);
      return;
    }

    getAccessTokenSilently()
      .then((token) => {
        const decoded = jwtDecode<TokenPayload>(token);
        setPermissions(decoded.permissions || []);
        setPermissionsLoaded(true);
      })
      .catch((error) => {
        console.warn('Error decoding token:', error);
        setPermissions([]);
        setPermissionsLoaded(true);
      });
  }, [auth0IsLoading, isAuthenticated, getAccessTokenSilently]);

  const getToken = useCallback(async () => {
    if (!isAuthenticated) {
      return null;
    }
    try {
      return await getAccessTokenSilently();
    } catch (error) {
      console.warn('Error fetching access token:', error);
      return null;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  const hasPermission = useCallback(
    (permission: PermissionString) => permissions.includes(permission),
    [permissions]
  );

  const isLoading = auth0IsLoading || !permissionsLoaded;

  // Stable deferred: a single Promise that resolves the first time `isLoading`
  // flips false. We hand `whenReady` to the router context so `beforeLoad` can
  // `await` initial auth resolution — once resolved, the awaiter reads the
  // latest auth snapshot from `latestResolvedRef`.
  const readyDeferredRef = useRef<{
    promise: Promise<ResolvedAuth>;
    resolve: (value: ResolvedAuth) => void;
  } | null>(null);
  if (readyDeferredRef.current === null) {
    let resolve!: (value: ResolvedAuth) => void;
    const promise = new Promise<ResolvedAuth>((res) => {
      resolve = res;
    });
    readyDeferredRef.current = { promise, resolve };
  }

  const latestResolvedRef = useRef<ResolvedAuth>({
    isAuthenticated,
    hasPermission,
  });
  latestResolvedRef.current = { isAuthenticated, hasPermission };

  useEffect(() => {
    if (!isLoading) {
      readyDeferredRef.current!.resolve(latestResolvedRef.current);
    }
  }, [isLoading]);

  const whenReady = useCallback(
    () =>
      readyDeferredRef.current!.promise.then(() => latestResolvedRef.current),
    []
  );

  const value = useMemo(
    () => ({
      permissions,
      hasPermission,
      isLoading,
      isAuthenticated,
      getToken,
      loginWithRedirect,
      logout,
      whenReady,
    }),
    [
      permissions,
      hasPermission,
      isLoading,
      isAuthenticated,
      getToken,
      loginWithRedirect,
      logout,
      whenReady,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
